import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import useButtonLogic from './useButtonLogic';
import Icon, { ICON_OPTIONS } from '../Icon';
import { PALETTE, SIZES } from '../../../../../../src/theme';

const BUTTON_OPTIONS = {
  ALIGN: {
    LEFT: 'left',
    CENTER: 'center',
  },
  COLORS: {
    NORMAL: PALETTE.NORMAL,
    LIGHT: PALETTE.LIGHT,
    PRIMARY: PALETTE.PRIMARY,
    SECONDARY: PALETTE.SECONDARY,
  },
  SIZES: {
    SM: SIZES.SM,
    MD: SIZES.MD,
    LG: SIZES.LG,
    XL: SIZES.XL,
    XXL: SIZES.XXL,
  },
  VARIANTS: {
    FILLED: 'filled',
    OUTLINED: 'outlined',
  },
};

const StyledButton = styled.button`
  align-items: center;
  display: flex;
  transition: ${props =>
    props.theme.transition.create([
      'background-color',
      'border-color',
      'box-shadow',
      'color',
    ])};
  &:enabled {
    cursor: pointer;
  }
  &:disabled {
    background-color: ${props => props.theme.palette[`${props.color}Light`]};
    box-shadow: ${props => props.theme.shadows[0]};
  }

  /** ALIGNMENT */
  ${props => {
    switch (props.align) {
      case BUTTON_OPTIONS.ALIGN.LEFT:
        return css`
          justify-content: flex-start;
        `;
      case BUTTON_OPTIONS.ALIGN.CENTER:
      default:
        return css`
          justify-content: center;
        `;
    }
  }}

  /** SIZE */
  ${props => {
    switch (props.size) {
      case BUTTON_OPTIONS.SIZES.SM:
        return css`
          border-radius: 24px;
          font-size: ${props.theme.fontSize.sm};
          padding: 4px 12px;
        `;
      case BUTTON_OPTIONS.SIZES.MD:
      default:
        return css`
          border-radius: 32px;
          font-size: ${props.theme.fontSize.sm};
          padding: 6px 16px;
        `;
      case BUTTON_OPTIONS.SIZES.LG:
        return css`
          border-radius: 44px;
          font-size: ${props.theme.fontSize.md};
          padding: 8px 22px;
        `;
      case BUTTON_OPTIONS.SIZES.XL:
        return css`
          border-radius: 64px;
          font-size: ${props.theme.fontSize.md};
          padding: 12px 32px;
        `;
      case BUTTON_OPTIONS.SIZES.XXL:
        return css`
          border-radius: 88px;
          font-size: ${props.theme.fontSize.lg};
          padding: 16px 44px;
        `;
    }
  }}

  /** VARIANT AND COLOR */
  ${props => {
    switch (props.variant) {
      case BUTTON_OPTIONS.VARIANTS.OUTLINED:
        return css`
          background: transparent;
          border: 1px solid ${props.theme.palette[props.color]};
          color: ${props.theme.palette[props.color]};
          &:hover:enabled {
            box-shadow: ${props.theme.shadows[2]};
          }
        `;
      case BUTTON_OPTIONS.VARIANTS.FILLED:
      default:
        return css`
          background-color: ${props.theme.palette[props.color]};
          border: none;
          box-shadow: ${props.theme.shadows[2]};
          color: ${props.theme.text.getContrastText(props.color)};
          text-shadow: ${props.theme.colors.lightOrDark(props.color) === 'dark'
            ? props.theme.shadow.textSubtlePop
            : undefined};
          &:hover:enabled {
            box-shadow: ${props.theme.shadows[6]};
          }
        `;
    }
  }}

  /** WIDTH */
  ${props =>
    props.fullWidth
      ? css`
          width: 100%;
        `
      : css`
          width: fit-content;
        `}
`;

const StyledIcon = styled(Icon)`
  /** SIZE */
  ${props => {
    switch (props.size) {
      case BUTTON_OPTIONS.SIZES.XS:
        return css`
          font-size: ${props.theme.fontSize.sm};
          margin-right: 8px;
        `;
      case BUTTON_OPTIONS.SIZES.SM:
        return css`
          font-size: ${props.theme.fontSize.sm};
          margin-right: 10px;
        `;
      case BUTTON_OPTIONS.SIZES.MD:
      default:
        return css`
          font-size: ${props.theme.fontSize.sm};
          margin-right: 12px;
        `;
      case BUTTON_OPTIONS.SIZES.LG:
      case BUTTON_OPTIONS.SIZES.XL:
        return css`
          font-size: ${props.theme.fontSize.md};
          margin-right: 12px;
        `;
      case BUTTON_OPTIONS.SIZES.XXL:
        return css`
          font-size: ${props.theme.fontSize.lg};
          margin-right: 12px;
        `;
    }
  }}
`;

const Button = forwardRef(
  (
    {
      align,
      className,
      color,
      disabled,
      handleClick,
      icon,
      iconColor: iconColorProp,
      fullWidth,
      size,
      text,
      type,
      variant,
    },
    ref
  ) => {
    const { colorShade } = useButtonLogic({ color });

    const iconColor = useMemo(() => {
      if (iconColorProp) return iconColorProp;
      switch (variant) {
        case BUTTON_OPTIONS.VARIANTS.FILLED:
          return colorShade === 'light'
            ? ICON_OPTIONS.COLORS.DARK
            : ICON_OPTIONS.COLORS.LIGHT;
        case BUTTON_OPTIONS.VARIANTS.OUTLINED:
        default:
          return color;
      }
    }, [color, colorShade, iconColorProp, variant]);

    return (
      <StyledButton
        align={align}
        color={color}
        className={className}
        disabled={disabled}
        icon={icon}
        fullWidth={fullWidth}
        onClick={handleClick}
        ref={ref}
        size={size}
        type={type}
        variant={variant}
      >
        {icon && <StyledIcon color={iconColor} icon={icon} size={size} />}
        {text}
      </StyledButton>
    );
  }
);

const buttonPropTypes = {
  align: PropTypes.oneOf(Object.values(BUTTON_OPTIONS.ALIGN)),
  className: PropTypes.string,
  color: PropTypes.oneOf(Object.values(BUTTON_OPTIONS.COLORS)),
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  handleClick: PropTypes.func,
  icon: PropTypes.string,
  iconColor: PropTypes.oneOf(Object.values(ICON_OPTIONS.COLORS)),
  size: PropTypes.oneOf(Object.values(BUTTON_OPTIONS.SIZES)),
  text: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit']),
  variant: PropTypes.oneOf(Object.values(BUTTON_OPTIONS.VARIANTS)),
};
const buttonDefaultProps = {
  align: BUTTON_OPTIONS.ALIGN.CENTER,
  className: undefined,
  color: BUTTON_OPTIONS.COLORS.PRIMARY,
  disabled: false,
  fullWidth: false,
  handleClick: undefined,
  icon: undefined,
  iconColor: undefined,
  size: BUTTON_OPTIONS.SIZES.MD,
  text: undefined,
  type: 'button',
  variant: BUTTON_OPTIONS.VARIANTS.FILLED,
};

Button.propTypes = buttonPropTypes;
Button.defaultProps = buttonDefaultProps;
export default Button;
export { buttonPropTypes, buttonDefaultProps, BUTTON_OPTIONS };
